// 在 React 展示层翻译原始文案；保留原始 props，切换语言不会重挂载表单。
// 不翻译 value / defaultValue / URL / 标识符；translate="no" 保护用户内容和原始日志。
import { createContext, createElement, forwardRef, Fragment, useContext, type ReactNode } from "react";
import { Link, NavLink } from "react-router-dom";
import { translate, useLocale, type Locale } from "./locale";

const Scope = createContext(true);
type HostProps = { tag: string; originalProps: Record<string, unknown> };
const ATTRIBUTES = ["title", "placeholder", "alt", "aria-label", "aria-description"];
function childrenInLocale(children: unknown, lang: Locale): unknown {
  if (typeof children === "string") return translate(children, lang);
  if (!Array.isArray(children)) return children;
  if (children.every((child) => typeof child === "string" || typeof child === "number")) {
    const joined = children.join("");
    const result = translate(joined, lang);
    if (result !== joined) return result;
  }
  return children.map((child) => childrenInLocale(child, lang));
}
function LocalizedContent({ children }: { children: ReactNode }) {
  const lang = useLocale();
  const enabled = useContext(Scope);
  const translated = enabled ? childrenInLocale(children, lang) : children;
  return createElement(Fragment, null, ...(Array.isArray(translated) ? translated : [translated]) as ReactNode[]);
}
// Router 的链接由库内部创建 <a>，在传入 children 时接上语言订阅。
export function linkProps(type: unknown, props: unknown): unknown {
  if (type !== Link && type !== NavLink) return props;
  const p = props as Record<string, unknown>;
  if (typeof p.children === "function") return props;
  return { ...p, children: createElement(LocalizedContent, { children: p.children as ReactNode }) };
}
export const LocalizedHost = forwardRef<unknown, HostProps>(function LocalizedHost({ tag, originalProps }, ref) {
  const lang = useLocale();
  const inherited = useContext(Scope);
  const enabled = inherited && originalProps.translate !== "no" && tag !== "pre";
  const props: Record<string, unknown> = { ...originalProps, ref };
  if (enabled) {
    for (const key of ATTRIBUTES) {
      if (typeof props[key] === "string") props[key] = translate(props[key] as string, lang);
    }
    if (!["textarea", "script", "style"].includes(tag)) props.children = childrenInLocale(props.children, lang);
  }
  const children = props.children;
  delete props.children;
  const element = createElement(tag, props, ...(Array.isArray(children) ? children : [children]) as ReactNode[]);
  return enabled === inherited ? element : createElement(Scope.Provider, { value: enabled }, element as ReactNode);
});
